import {useCallback, useState} from 'react';

import {Box, Typography} from '@mui/material';
import './styles';
import {useTranslation} from 'react-i18next';

import {ADD_TEEN_EVENTS} from '@/app/auth/events/AddTeenEvents';
import AccountLinkModal from '@/app/main/onboarding/components/AccountLinkModal/AccountLinkModal';
import FamilyCodeModal from '@/app/main/onboarding/components/FamilyCodeModal/FamilyCodeModal';
import {AppButton} from '@/app/shared-components/AppButton/AppButton';
import {AppButtonVariant} from '@/app/shared-components/AppButton/models/interfaces';
import {C_ORIGIN} from '@/constants/OriginConstants';
import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useTrackingManager} from '@/hooks/TrackingManager/useTrackingManager';

interface FamilyCodeBannerProps {
  kidName?: string;
  onKidLinked?: () => void;
  isAddKidLastStep?: boolean;
}

const FamilyCodeBanner: React.FC<FamilyCodeBannerProps> = ({
  kidName,
  onKidLinked,
  isAddKidLastStep = false,
}) => {
  const {t} = useTranslation();
  const {trackEvent} = useTrackingManager();

  const [showModal, setShowModal] = useState(false);
  const [isAccountLinkModalOpen, setIsAccountLinkModalOpen] =
    useState<boolean>(false);

  const trackEventOpenForm = useCallback(() => {
    trackEvent(ADD_TEEN_EVENTS.SELECT_ADD_KID_ADD_FAMILY_CODE, {
      project_tag: PROJECT_TAGS.ONBOARDING,
      origin: isAddKidLastStep
        ? C_ORIGIN.ADD_KID_LAST_STEP
        : C_ORIGIN.ADD_KID_FIRST_STEP,
    });
  }, [isAddKidLastStep, trackEvent]);

  const handleOnClickAddFamilyId = useCallback(() => {
    trackEventOpenForm();
    setShowModal(true);
  }, [trackEventOpenForm]);

  const handleOnCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleOnKidLinked = useCallback(() => {
    handleOnCloseModal();
    setIsAccountLinkModalOpen(true);
  }, [handleOnCloseModal]);

  const onCloseAccountLinkModal = useCallback(() => {
    setIsAccountLinkModalOpen(false);
    onKidLinked?.();
  }, [onKidLinked]);

  return (
    <>
      <Box className="family-code-banner-container">
        <Box className="family-code-banner-container-content">
          <Box className="family-code-banner-container-content-frame">
            <Box className="family-code-banner-container-content-frame-title-container">
              <Typography className="family-code-banner-container-content-title">
                {t('family_code_banner.title', {
                  name: kidName || t('your_kid'),
                })}
              </Typography>
              <Typography className="family-code-banner-container-content-subtitle">
                {t('family_code_banner.sub_title')}
              </Typography>
            </Box>
            <AppButton
              label={t('family_code_banner.button')}
              variant={AppButtonVariant.onSecondary}
              onClick={handleOnClickAddFamilyId}
              isAuto
            />
          </Box>
        </Box>
      </Box>
      <FamilyCodeModal
        open={showModal}
        onClose={handleOnCloseModal}
        kidName={kidName}
        onKidLinked={handleOnKidLinked}
      />
      <AccountLinkModal
        firstName={kidName}
        open={isAccountLinkModalOpen}
        onClose={onCloseAccountLinkModal}
        onContinue={onCloseAccountLinkModal}
      />
    </>
  );
};

export default FamilyCodeBanner;
